import { useCallback, useEffect, useState } from "react";
import { Send, Pause, Play } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { RippBadge } from "./primitives";
import { VoicePreview } from "./VoicePreview";
import { TokenLaunchSwitch } from "./TokenLaunchSwitch";
import { RippLaunchPanel } from "./RippLaunchPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIND_TONE = { proof: "emerald", take: "cyan", meme: "amber", build: "cyan", launch: "red" };

export const XVoice = () => {
  const [st, setSt] = useState(null);
  const [posts, setPosts] = useState([]);
  const [aud, setAud] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([axios.get(`${API}/voice/status`), axios.get(`${API}/voice/posts?limit=4`)]);
      setSt(s.data); setPosts(p.data.posts || []);
    } catch { /* panel stays in its last state */ }
  }, []);
  const loadAudience = useCallback(async () => {
    try { const r = await axios.get(`${API}/voice/audience?limit=40`); setAud(r.data); } catch { /* keep last */ }
  }, []);
  useEffect(() => { load(); loadAudience(); const id = setInterval(load, 30000); const id2 = setInterval(loadAudience, 300000); return () => { clearInterval(id); clearInterval(id2); }; }, [load, loadAudience]);
  const toggle = async () => {
    const r = await axios.post(`${API}/voice/config`, { enabled: !st?.enabled }); setSt(r.data);
    toast(r.data.enabled ? "RIPP Voice resumed — posting autonomously" : "RIPP Voice paused — no posts until resumed");
  };
  const postNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/voice/post-now`, {});
      toast(r.data.ok ? `Posted (${r.data.kind}): ${r.data.text.slice(0, 80)}…` : `Not posted — ${r.data.error}`);
      load();
    } catch (e) { toast(`Post failed — ${e.response?.data?.detail || e.message}`); }
    setBusy(false);
  };
  const connectX = async () => {
    try {
      const r = await axios.get(`${API}/voice/x/oauth/start`);
      if (r.data.ok) window.open(r.data.url, "_blank", "noopener"); else toast(`Connect X failed — ${r.data.error}`);
    } catch (e) { toast(`Connect X failed — ${e.response?.data?.detail || e.message}`); }
  };
  const inviteFollowers = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/voice/invite-followers`, { dry_run: false });
      toast(`Invited ${r.data.sent} of ${r.data.followers} followers${r.data.failed?.length ? ` · ${r.data.failed.length} couldn't receive DMs` : ""}`);
    } catch (e) { toast(`Invite failed — ${e.response?.data?.detail || e.message}`); }
    setBusy(false);
  };
  if (!st) return <div className="text-[9px] font-mono text-[var(--r2-text2)]">loading…</div>;
  const nextIn = Math.max(0, Math.round((st.next_post_at * 1000 - Date.now()) / 60000));
  return (
    <div className="flex items-start gap-3 h-full" data-testid="v2-voice">
      <div className="shrink-0 min-w-[190px] space-y-1">
        <div className="flex items-center gap-1.5">
          <RippBadge tone={!st.configured ? "red" : st.enabled ? "emerald" : "amber"} pulse={st.enabled && st.configured} testid="voice-state">
            {!st.configured ? "X NOT CONNECTED" : st.enabled ? "AUTOPOSTING" : "PAUSED"}
          </RippBadge>
          <span className="text-[9px] font-mono text-[var(--r2-text2)]">@{st.handle || "—"}</span>
          {!st.configured && st.oauth2_app_ready && (
            <button onClick={connectX} data-testid="voice-connect-x" className="flex items-center gap-1 rounded-sm border border-[var(--r2-cyan)] px-1.5 py-[2px] text-[8px] font-bold tracking-[0.14em] text-[var(--r2-cyan)]">CONNECT X</button>
          )}
          {st.configured && st.oauth2_app_ready && (
            <button onClick={connectX} data-testid="voice-reconnect-x" title="Re-authorise X (adds DM permission for follower invites)" className="rounded-sm border border-[var(--r2-border)] px-1.5 py-[2px] text-[8px] font-bold tracking-[0.14em] text-[var(--r2-text2)] hover:text-[var(--r2-cyan)]">RECONNECT X</button>
          )}
          {st.configured && (
            <button onClick={inviteFollowers} disabled={busy} data-testid="voice-invite-followers" className="rounded-sm border border-[var(--r2-cyan)] px-1.5 py-[2px] text-[8px] font-bold tracking-[0.14em] text-[var(--r2-cyan)] disabled:opacity-40">INVITE FOLLOWERS</button>
          )}
        </div>
        <div className="text-[9px] font-mono text-[var(--r2-text2)]" data-testid="voice-counts">
          {st.posts_today}/{st.posts_per_day} today · {st.posts_30d}/{st.monthly_cap} this month{st.replies ? ` · replies ${st.replies.today}/${st.replies.max_per_day}` : ""}{st.enabled && st.configured ? ` · next ${st.in_window ? `in ~${nextIn}m` : "when the window opens"}` : ""}
        </div>
        <div className="flex gap-1.5 pt-0.5">
          <button onClick={toggle} data-testid="voice-toggle" className="flex items-center gap-1 rounded-sm border border-[var(--r2-border)] px-1.5 py-[2px] text-[8px] font-bold tracking-[0.14em] text-[var(--r2-text2)] hover:text-[var(--r2-text1)]">{st.enabled ? <><Pause size={10} /> PAUSE</> : <><Play size={10} /> RESUME</>}</button>
          <button onClick={postNow} disabled={busy || !st.configured} data-testid="voice-post-now" className="flex items-center gap-1 rounded-sm border border-[var(--r2-border)] px-1.5 py-[2px] text-[8px] font-bold tracking-[0.14em] text-[var(--r2-text2)] hover:text-[var(--r2-text1)] disabled:opacity-40"><Send size={10} /> POST NOW</button>
        </div>
        {st.last_error && <div className="text-[8.5px] font-mono text-[var(--r2-amber)] truncate" title={st.last_error} data-testid="voice-error">{st.last_error}</div>}
        {aud && (
          <div className="pt-1 space-y-[2px] text-[9px] font-mono" data-testid="voice-audience">
            <div className="text-[8px] tracking-[0.18em] text-[var(--r2-text2)]">AUDIENCE · LAST {aud.posts_measured} POSTS</div>
            <div className="text-[var(--r2-text1)]" data-testid="voice-followers">
              <span className="font-bold">{aud.followers ?? "—"}</span> followers
              {aud.followers_delta_7d != null && <span className={aud.followers_delta_7d >= 0 ? "text-[var(--r2-emerald)]" : "text-[var(--r2-red)]"}> {aud.followers_delta_7d >= 0 ? "+" : ""}{aud.followers_delta_7d} / 7d</span>}
            </div>
            <div className="text-[var(--r2-text2)]" data-testid="voice-impressions">
              {aud.totals?.impressions?.toLocaleString()} impressions · {aud.totals?.engagements} engagements{aud.totals?.engagement_rate_pct != null ? ` · ${aud.totals.engagement_rate_pct}% rate` : ""} · ~{aud.totals?.avg_impressions_per_post ?? 0}/post
            </div>
            <div className={`leading-tight ${aud.verdict?.startsWith("generating") ? "text-[var(--r2-emerald)]" : aud.verdict?.startsWith("early") ? "text-[var(--r2-cyan)]" : "text-[var(--r2-amber)]"}`} data-testid="voice-verdict" title={aud.verdict}>{aud.verdict}</div>
            {aud.error && <div className="text-[var(--r2-red)] truncate" title={aud.error}>{aud.error}</div>}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-1 overflow-hidden">
        <VoicePreview disabled={busy} onPosted={load} />
        <TokenLaunchSwitch token={st.token} onDone={load} />
        <RippLaunchPanel />
        {posts.length === 0 && <div className="text-[9px] font-mono text-[var(--r2-text2)]">No posts yet — the Voice agent drafts from real desk, meme and swarm facts only.</div>}
        {posts.map((p) => (
          <div key={p.ts} className="flex items-start gap-2 text-[9.5px] leading-tight" data-testid={`voice-post-${p.kind}`}>
            {p.media_id && <img src={`${API}/voice/media/${p.media_id}`} alt="" className="shrink-0 w-[46px] h-[26px] object-cover rounded-sm border border-[var(--r2-border)]" data-testid={`voice-post-media-${p.kind}`} />}
            <RippBadge tone={p.ok ? KIND_TONE[p.kind] || "cyan" : "red"}>{p.ok ? p.kind.toUpperCase() : "FAILED"}</RippBadge>
            <span className="truncate text-[var(--r2-text1)]" title={p.text}>{p.text}</span>
            {p.metrics && <span className="shrink-0 text-[8.5px] text-[var(--r2-text2)]" data-testid={`voice-post-metrics-${p.kind}`}>{p.metrics.impression_count ?? 0} views · {p.metrics.like_count ?? 0} ♥ · {p.metrics.reply_count ?? 0} re</span>}
            {p.tweet_id && <a className="shrink-0 text-[var(--r2-cyan)]" href={`https://x.com/${st.handle || "i"}/status/${p.tweet_id}`} target="_blank" rel="noreferrer">↗</a>}
            {!p.ok && <span className="shrink-0 text-[var(--r2-red)] truncate max-w-[160px]" title={p.error}>{p.error}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};
